import React from 'react';
import SectionShell from '../SectionShell';
import Reveal from '../Reveal';
import { useT } from '../../i18n/LanguageContext';

/**
 * Folie 23 — "Der traditionelle Weg".
 * 8 circular photos (image66, 65, 64, 63, 62, 60, 61, 59 — order from PPTX
 * left→right by x-position) sitting above numbered pennants 01–08 on a
 * dashed base line. Reproduces the PPTX template natively (image58 is the
 * blank template shell — we recreate its structure with clean CSS).
 */
const STEPS = [
  { n: '01', img: 'image66.png' },
  { n: '02', img: 'image65.png' },
  { n: '03', img: 'image64.png' },
  { n: '04', img: 'image63.png' },
  { n: '05', img: 'image62.png' },
  { n: '06', img: 'image60.png' },
  { n: '07', img: 'image61.png' },
  { n: '08', img: 'image59.png' },
];

export default function SectionTraditionellerWeg() {
  const { t } = useT();
  const m = t.tradWeg;

  return (
    <SectionShell id="trad-weg" className="">
      <div className="mb-10">
        <Reveal>
          <div className="overline mb-4">{m.overline}</div>
          <div className="flex flex-wrap items-end justify-between gap-6">
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-white whitespace-nowrap">
              {m.title}
            </h2>
            <p className="font-ui text-2xl lg:text-3xl text-white/70 leading-relaxed max-w-[48ch]">
              {m.sub}
            </p>
          </div>
        </Reveal>
      </div>

      <Reveal delay={160}>
        <div data-testid="trad-weg-flow" className="relative w-full overflow-x-auto pb-4">
          <div className="min-w-[1100px]">
            {/* Row 1 — circles */}
            <div
              className="grid gap-3 md:gap-4"
              style={{ gridTemplateColumns: 'repeat(8, minmax(0, 1fr))' }}
            >
              {STEPS.map((s, i) => {
                const label = m.steps?.[i];
                return (
                  <div key={`c-${s.n}`} className="flex justify-center">
                    <div className="trad-circle relative" style={{ animationDelay: `${i * 90}ms` }}>
                      <img
                        src={`/source-pptx/${s.img}`}
                        alt={label?.title || ''}
                        loading="lazy"
                        draggable={false}
                        className="block w-full h-full object-cover select-none"
                      />
                      <span className="trad-circle-ring" aria-hidden />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Row 2 — dashed line + pennants on top */}
            <div className="trad-pennant-row">
              <div
                className="grid gap-3 md:gap-4 relative z-10"
                style={{ gridTemplateColumns: 'repeat(8, minmax(0, 1fr))' }}
              >
                {STEPS.map((s) => (
                  <div key={`p-${s.n}`} className="flex justify-center">
                    <div className="trad-pennant" aria-hidden>
                      <span className="trad-pennant-num">{s.n}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Row 3 — labels (single unified font size for title + subtitle) */}
            <div
              className="grid gap-3 md:gap-4 mt-4"
              style={{ gridTemplateColumns: 'repeat(8, minmax(0, 1fr))' }}
            >
              {STEPS.map((s, i) => {
                const label = m.steps?.[i];
                return (
                  <div key={`l-${s.n}`} className="text-center px-1" data-testid={`trad-step-${s.n}`}>
                    {label && (
                      <>
                        <div className="font-display font-bold text-lg md:text-xl leading-tight text-[#f2d979]">
                          {label.title}
                        </div>
                        <div className="font-ui text-base md:text-lg leading-snug mt-1 text-white/65">
                          {label.subtitle}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </Reveal>
    </SectionShell>
  );
}
